<template>
  <div class="customcheckerboard-style-selection row">
    <template v-if="displayModels && $props.canvasLoaded">
      <h5
        :class="[
          'customcheckerboard-main-views-wrapper-title',
          'text-center',
        ]">{{ translations.customCheckerboardStyleTitle }}</h5>

      <button
        v-for="style in styles"
        :key="style.model"
        @click="selectStyle(style)"
        :disabled="!enableControls"
        class="style-button columns small-6 medium-4"
      >
        <div class="style-button-svg">
          <svg-renderer
            @isLoading="setLoadedSvg"
            @svgSelected="selectModel"
            :isSvgSelected="selectedStyle === style.model"
            :images="images[style.model]"
            :model="style.model"
            :view="style.svg_view"
            :enableSmartcrop="false"
            package="customcheckerboard"
          ></svg-renderer>
        </div>
        <img
          v-show="style.model === selectedStyle"
          class="customcheckerboard-style-selection-selected"
          src="//images.vans.com/is/image/VansBrand/icon-checkmark?$SCALE-ORIGINAL$&amp;fmt=png8-alpha"
        />
        <span class="style-button-name">{{ style.name }}</span>
      </button>
    </template>
  </div>
</template>

<script>

import svgRenderer from '@/components/SvgRenderer';
import { checkerboardCombination } from '@/components/CustomCheckerboard/MainViews/SizeSelector/combinations';
import { warps } from '../WarpSelector/warpSettings'
import PlacementHelper from '../PlacementHelper';
import { getLocale } from "@/data/regionInfo";
import customsTools from '@/mixins/CustomsTools';

export default {
  extends: PlacementHelper,
  mixins: [ customsTools ],
  components: {
    svgRenderer
  },
  props: {
    canvasLoaded: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      stylesData: [],
      selectedStyle: null,
      images: {},
      isSvgLoaded: 0,
      model: null,
      placementImage: null,
      displayModels: true,
    }
  },
  computed: {
    translations(){
      return this.$store.state.translations.data;
    },
    styles() {
      return Object.keys(this.stylesData).map((item) => {
        return {
          model: item,
          name: this.stylesData[item].display_name,
          ...this.stylesData[item]
        }
      })
    },
    enableControls() {
      return this.$store.state.customCheckerboard.enableControls;
    },
    noEsiActive() {
      return this.$store.getters['utils/noEsiActive'];
    },
    canvasActive() {
      return this.$store.getters["utils/canvasActive"];
    }
  },
  watch: {
    /**
     * This watch is useful when the warp view is skipped.
     * It means the canvas is not loaded when the user
     * clicks next. The initFlow inside created method won't be called
     * since when the component is rendered from size selector view
     * the canvas is not loaded yet. In a regular flow, the prop will be true
     * when the component is created, it means the initFlow in created
     * method will be executed.
     */
    canvasLoaded: function(newValue) {
      if (newValue) {
        this.initFlow();
      }
    },
  },
  methods: {
    setLoadedSvg(val) {
      if (!val) {
        this.isSvgLoaded++;
        if (this.isSvgLoaded == this.styles.length) {
          this.$store.commit("customCheckerboard/SET_ENABLE_CONTROLS", true);
        }
      }
    },
    generatePlacementImage(image, component, warp, model) {
      return this.loadAndCropGeneratedImage(model, component, warp)
        .then(imageUrl => {
          if (!this.images[model]) this.images[model] = {};
          this.images = { ...this.images, [model]: { ...this.images[model], [component]: imageUrl }};
        })
        .catch(console.error);
    },
    async initPlacementImages(image) {
      const { canvasConfig: { warp } } = this.$store.state.customCheckerboard;
      return Promise.all(this.styles.map(async ({ ugc_component_name, secondary_ugc_component_name, model }) => {
        if (ugc_component_name) {
          return this.generatePlacementImage(image, ugc_component_name, warp, model)
            .then(() => {
              if (secondary_ugc_component_name) {
                return this.generatePlacementImage(image, secondary_ugc_component_name, warp, model);
              }
            });
        }
      }));
    },
    async selectModel({ placementImage }) {
      this.placementImage = placementImage;
      this.$store.commit("customCheckerboard/SET_ENABLE_CONTROLS", false);
      try {
        const recipeId = await this.generateRecipeId();
        this.redirectToCustomizer(recipeId);
      } catch (error) {
        this.$store.commit("customCheckerboard/SET_ENABLE_CONTROLS", true);
        console.error('Error generating the recipe', error);
      }
    },
    selectStyle(style) {
      const productType = style.product_type;
      this.$store.commit('customCheckerboard/SET_PRODUCT_TYPE', {productType});
      if (this.$store.getters['utils/useEcomDataLayerFormat']) {
        this.$checkerboardDataLayer.updateCategoryType(productType);
        this.$checkerboardDataLayer.logAnalyticsEvent({action: 'Select Style', eventLabel: style.name});
      } else {
        dataLayer.push({
          'event': 'trackEvent',
          'eventCategory': 'customs',
          'eventAction': 'checkerboard - external flow',
          'eventLabel': `select style: ${ style.model }`
        });
      }

      this.selectedStyle = style.model;
      this.model = this.selectedStyle;
    },
    redirectToCustomizer(recipeID) {

      let url = this.canvasActive ? 
      `${this.$store.getters["utils/baseUrl"]("customcheckerboard")}/${getLocale().replace("_","-")}/customizer/${ this.model }?recipe=${ recipeID }` :
      `${this.$store.getters["utils/baseUrl"]("customcheckerboard")}/customizer.${ this.model }.html?recipe=${ recipeID }`;

      url = this.persistQueryParams(url, { source : this.$store.state.customCheckerboard.source});

      this.$store.commit('customCheckerboard/RESET_NAVIGATION');
      if(this.canvasActive ) {
        window.parent.location = url;
      } else {
        window.location = url;
      }
    },

    async updateRecipeByComponent(componentName, size) {
      // generates the placement and the ugc swatch
      const ugcTemp = await this.generatePlacementAndUgcSwatch(
        this.placementImage[componentName],
        componentName,
        size
      );
      const { warp, size : sizeCanvas } = this.canvasConfig;
      this.$recipe.addToolkitOptions(componentName, warps[warp].id, checkerboardCombination[sizeCanvas][warp].swatchId);
      this.$recipe.updateUGC(componentName, ugcTemp, 'custom-checkerboard');
    },

    async generateRecipeId() {
      const pdm = await this.$store.dispatch("customCheckerboard/GET_PDM", { pdmModel: this.model });
      const recipe_v2 = await this.$store.dispatch("recipe/GET_RECIPE", {
        recipe_id: pdm.default_recipe,
        type: 'customcheckerboard',
        setData: true
      });

      // saves the generated image in the server
      const uploadedImage = await this.uploadGeneratedImage();

      await this.updateRecipeByComponent(
        this.stylesData[this.model].ugc_component_name,
        pdm.ugcDefaultSize
      );

      if (this.stylesData[this.model].secondary_ugc_component_name) {
        await this.updateRecipeByComponent(
          this.stylesData[this.model].secondary_ugc_component_name,
          pdm.ugcDefaultSize
        );
      }

      const { recipeId } = await this.$store.dispatch("recipe/SAVE_RECIPE", {
        recipe: this.$store.state.recipe.recipe_v2,
        requestParams: { locale: getLocale() },
        type: 'customcheckerboard'
      });
      return recipeId;
    },
    initFlow() {
      const { availableModels } = this.$store.state.customCheckerboard;
      this.$store
        .dispatch('customCheckerboard/FETCH_PRODUCT_DATA', { availableModels })
        .then((data) => {
          this.stylesData = data;
          const onlyOneModel = availableModels.length === 1
          // checks if there's more than one model to display to show/hide the models images
          if (onlyOneModel) {
            const productType = this.styles[0].product_type;
            this.$store.commit('customCheckerboard/SET_PRODUCT_TYPE', {productType});
            if (this.$store.getters['utils/useEcomDataLayerFormat'])
              this.$checkerboardDataLayer.updateCategoryType(productType);
            this.displayModels = false;
          }
          const { activeUGCComponent } = this.$store.state.customCheckerboard;
          this.initPlacementImages(this.wrapImage().src)
            .then(() => {
              // when there's only one model to choose from hide the SVG images,
              // generate the recipe and redirect to Customizer with the only
              // model selected
              if (onlyOneModel) {
                this.selectStyle(this.styles[0]);
                this.selectModel({ placementImage: this.images[this.model] });
              }
            });
        })
    }
  },
  created() {
    if (this.$props.canvasLoaded) {
      this.initFlow();
    }
  },
  beforeDestroy() {
     if(this.isMainApp) { console.log("destroy listener");  window.removeEventListener('pagehide');}
  }
}
</script>
